import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {isFeatureEnabled} from 'feature-flags';

import {useDispatch} from 'react-redux';

import {head, isNil, reject, take} from 'ramda';

import type {ListAutomationRulesFilter} from '@dms/api/orchestration';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {Nullish} from 'shared/web';

const NAMED_RULE_LIMIT = 5;
const RULE_NAME_SEPARATOR = ', ';
const MORE_RULES_SUFFIX = ' …';

type AutomationRuleUsage = Required<Pick<ListAutomationRulesFilter, 'customFieldId'>> &
  Pick<ListAutomationRulesFilter, 'optionId'>;

type AutomationRuleUsageWarningKey =
  | 'page.generalSettings.labels.deleteDefinitionAutomationRulesWarning'
  | 'page.generalSettings.labels.deleteEnumValueAutomationRulesWarning';

/**
 * Deleting a custom field or one of its options stays allowed, but every
 * automation rule that uses it can then no longer be activated or resumed.
 * The warning names those rules. Any failure of the rule list (403 included)
 * yields no warning, so the delete dialog never depends on the
 * automation-rules module. The rule API loads on the first delete, so it
 * stays out of the initial bundle.
 */
export function useAutomationRuleUsageWarning(warningKey: AutomationRuleUsageWarningKey) {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const [canReadSettings] = usePermissions({permissionKeys: ['readSettings']});

  return (usage: AutomationRuleUsage): Promise<string | Nullish> => {
    if (!isFeatureEnabled(featureFlags.ORCHESTRATION_MODULE) || !canReadSettings) {
      return Promise.resolve(null);
    }

    return import('@dms/api/orchestration')
      .then(({orchestrationApi}) => {
        const request = dispatch(orchestrationApi.endpoints.listAutomationRules.initiate(usage));

        return request
          .unwrap()
          .then((data) => {
            const page = head(data.pages);
            if (isNil(page) || page.pagination.total === 0) {
              return null;
            }

            const names = reject(
              isNil,
              take(NAMED_RULE_LIMIT, page.items).map((rule) => rule.name)
            ).join(RULE_NAME_SEPARATOR);

            return i18n.t(warningKey, {
              count: page.pagination.total,
              names:
                page.pagination.total > NAMED_RULE_LIMIT ? `${names}${MORE_RULES_SUFFIX}` : names,
            });
          })
          .finally(() => request.unsubscribe());
      })
      .catch(() => null);
  };
}
